import "./InputField.scss";
import React, { useEffect, useState } from "react";
import { commatizeNum } from "../../../utilities/TextFormatter";
import Info from "../Info";
import { useValidationManager } from "../../../hooks/useValidation";

export function CheckboxInputField({
  field,
  fieldName,
  value,
  validationName,
  title,
  info,
  subTitle,
  placeholder,
  formatter,
  maxLength,
  onChange, //fires when you change
  validateOnBlur,
  forceValidate,
  onUpdate, //fires when you lose focus
  groupId, //check why this is gray
  isDisabled,
  notifyAboveOfValidationErrs,
  currentData,
  disableError,
  customValidate,
  children,
  validateChildren,
  sequence,
  props,
}) {
  const [validationErrors, setValidationErrors] = useState([]);
  const [childElement, setChildElement] = useState(null);
  const validationMgr = useValidationManager(forceValidate, groupId);

  const updatedValidationName =
    validationName?.toLowerCase() ?? `${groupId}_${fieldName}`.toLowerCase();
  const isRequired =
    validationMgr?.getConfig(updatedValidationName)?.isRequired &&
    validationMgr?.getConfig(updatedValidationName)?.groupId === groupId;
  //if it's a password
  const [passwordVisible, setIsPasswordVisible] = useState(false);

  let unitRightStyle = {
    top: "1.5rem",
  };

  if (subTitle) {
    unitRightStyle.top = "3rem";
  }

  useEffect(() => {
    if (validationMgr) {
      validationMgr.setForceValidate(forceValidate);
    }

    if (forceValidate) {
      handleValidate(value);
    }
  }, [forceValidate]);

  useEffect(() => {
    if (validateChildren) {
      handleValidateChildChange(value);
    }
  }, [value]);

  useEffect(() => {
    if (children) {
      let elementClone = {
        ...children.props,
        value: value,
        onBlur: onBlur,
      };

      if (onChange) {
        elementClone.onChange = (e, field) => {
          if (e?.target) {
            handleOnChange(e.target.value, field?.FieldName ?? fieldName);
          }
        };
      }

      if (onUpdate) {
        elementClone.onUpdate = onUpdate;
      }

      if (isDisabled) {
        elementClone.disabled = isDisabled;
      }

      elementClone.field = {
        PlaceHolder: placeholder,
        FieldName: fieldName,
      };

      if (validationErrors) {
        elementClone.error = validationErrors.length > 0 ? "true" : "false";
      }

      const child = React.cloneElement(children, elementClone);
      setChildElement(child);
    }
  }, [value, children, validationErrors]);

  useEffect(() => {
    if (validationErrors && validationErrors.length > 0) {
      if (notifyAboveOfValidationErrs) {
        notifyAboveOfValidationErrs(true);
      }
    } else if (validationErrors && validationErrors.length === 0) {
      if (notifyAboveOfValidationErrs) {
        notifyAboveOfValidationErrs(false);
      }
    }
  }, [validationErrors]);

  const applyFormatter = (value) => {
    switch (formatter ? formatter.toUpperCase() : "") {
      case "COMMA":
        return commatizeNum(value);
      case "":
        return value;
      default:
        return value;
    }
  };
  const handleValidate = (currentValue) => {
    if (formatter === "COMMA" && currentValue) {
      currentValue = currentValue.replace(/,/g, "");
    }
    let validationResult = null;
    if (validateOnBlur || forceValidate) {
      if (!customValidate) {
        validationResult = validationMgr.runValidation(
          currentValue,
          updatedValidationName,
          currentData
        );
      } else {
        validationResult = customValidate(currentValue, currentData);
      }
      setValidationErrors(validationResult.messages);
    }
  };

  const handleOnChange = (currentValue, e) => {
    if (formatter && formatter.toUpperCase() === "COMMA") {
      currentValue = currentValue.replace(/,/g, "");
    }
    if (
      validationMgr.runFieldMask(currentValue, updatedValidationName, maxLength)
    ) {
      if (currentValue && currentValue !== "") {
        setValidationErrors([]);
      }

      if (onChange) {
        onChange(currentValue, fieldName);
      }
    }
  };

  const handleValidateChildChange = (currentValue) => {
    if (formatter && formatter.toUpperCase() === "COMMA") {
      currentValue = currentValue.replace(/,/g, "");
    }

    if (
      validationMgr.runFieldMask(currentValue, updatedValidationName, maxLength)
    ) {
      if (currentValue && currentValue !== "") {
        setValidationErrors([]);
      }
    }
  };

  const onBlur = (e) => {
    if (validateOnBlur) handleValidate(e.target.value);

    if (onUpdate) onUpdate(e.target.value, fieldName);
  };

  let id = "field_cbx_" + field?.FieldName ?? fieldName + "_" + sequence ?? 1;
  const isChecked =
    value !== undefined && value !== null
      ? value == "1" ||
        value === true ||
        value.toString().toLowerCase() === "true" ||
        value.toString().toLowerCase() === "yes" ||
        value.toString().toLowerCase() === "y"
      : false;

  return (
    <div className="field-input checkbox-field-input">
      <div
        className={`generic-input-component cbx_control`}
        title={title && !info ? title.replace("*", "") : ""}
        field-name={fieldName}
      >
        <div className="title-header">
          <div className="title-row">
            <input
              id={id}
              key={id}
              type="checkbox"
              onBlur={(context) => (onBlur ? onBlur(context, field) : null)}
              checked={isChecked}
              disabled={isDisabled}
              onChange={(context) => {
                if (onChange) {
                  onChange(context.target.checked, field);
                }
              }}
              {...props}
            />
            <div className={`left`}>
              {title}
              {isRequired ? "*" : ""}
            </div>
          </div>
          {subTitle ? (
            (typeof subTitle).toLowerCase() === "string" ? (
              <div
                className={"subtitle"}
                dangerouslySetInnerHTML={{ __html: subTitle }}
              ></div>
            ) : (
              <div className={"subtitle"}>{subTitle}</div>
            )
          ) : null}
        </div>
      </div>
      {!disableError && validationErrors?.length > 0 && (
        <div className={"error-wrapper"}>
          {validationErrors.map((message) => (
            <span key={message} className={"error"}>
              {message}&nbsp;
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
